import { X } from 'lucide-react';

interface RulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function RulesModal({ isOpen, onClose }: RulesModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden border border-purple-500/30 shadow-2xl shadow-purple-500/20"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-purple-500/20 flex items-center justify-between bg-avalon-bg-dark/50">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-avalon-purple to-purple-600 flex items-center justify-center text-lg">
              📊
            </span>
            积分规则
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-avalon-text hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(85vh-80px)] space-y-5">
          <div>
            <h3 className="text-avalon-gold font-bold mb-2 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-avalon-gold/20 flex items-center justify-center text-sm">1</span>
              基础胜负积分
            </h3>
            <div className="space-y-2 text-avalon-text text-sm pl-8">
              <p className="flex items-start gap-2">
                <span className="text-blue-400 font-medium">正义胜利:</span>
                <span>所有正义玩家 <strong className="text-white">+2X 分</strong></span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-red-400 font-medium">邪恶胜利（刺杀）:</span>
                <span>所有邪恶玩家 <strong className="text-white">+2X 分</strong></span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-red-400 font-medium">邪恶胜利（破坏）:</span>
                <span>所有邪恶玩家 <strong className="text-white">+3X 分</strong></span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-gray-400 font-medium">失败方:</span>
                <span>所有玩家 <strong className="text-white">-X 分</strong></span>
              </p>
              <p className="text-xs text-avalon-text/60 mt-1">
                （X = 本局人数）
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-avalon-gold font-bold mb-2 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-avalon-gold/20 flex items-center justify-center text-sm">2</span>
              MVP 荣誉奖励
            </h3>
            <div className="space-y-2 text-avalon-text text-sm pl-8">
              <p>每局评选 1 名 MVP（由录入者指定）</p>
              <p className="flex items-start gap-2">
                <span className="text-blue-400 font-medium">正义阵营 MVP:</span>
                <span><strong className="text-white">+2X 分</strong></span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-red-400 font-medium">邪恶阵营 MVP:</span>
                <span><strong className="text-white">+X 分</strong></span>
              </p>
              <p>MVP 荣誉计入个人荣誉榜</p>
              <p className="text-xs text-avalon-text/60 mt-1">
                （X = 本局人数）
              </p>
            </div>
          </div>

          <div className="bg-avalon-card/30 rounded-xl p-4 border border-purple-500/20">
            <h3 className="text-avalon-gold font-bold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-avalon-gold/20 flex items-center justify-center text-sm">3</span>
              计算公式示例
            </h3>
            <div className="text-sm text-avalon-text space-y-2">
              <div>
                <p className="text-white font-medium">例 1：10 人局，正义胜利</p>
                <ul className="space-y-1 pl-4 text-avalon-text/80">
                  <li>• X = 10</li>
                  <li>• 正义方每人 = 2×10 + (MVP? +10 : 0) = <strong className="text-blue-400">20~30 分</strong></li>
                  <li>• 邪恶方每人 = <strong className="text-red-400">-10 分</strong></li>
                </ul>
              </div>
              <div>
                <p className="text-white font-medium">例 2：10 人局，邪恶胜利（刺杀）</p>
                <ul className="space-y-1 pl-4 text-avalon-text/80">
                  <li>• X = 10</li>
                  <li>• 邪恶方每人 = 2×10 + (MVP? +10 : 0) = <strong className="text-red-400">20~30 分</strong></li>
                  <li>• 正义方每人 = <strong className="text-blue-400">-10 分</strong></li>
                </ul>
              </div>
              <div>
                <p className="text-white font-medium">例 3：10 人局，邪恶胜利（破坏）</p>
                <ul className="space-y-1 pl-4 text-avalon-text/80">
                  <li>• X = 10</li>
                  <li>• 邪恶方每人 = 3×10 + (MVP? +10 : 0) = <strong className="text-red-400">30~40 分</strong></li>
                  <li>• 正义方每人 = <strong className="text-blue-400">-10 分</strong></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-xs text-avalon-text/50 text-center pt-2 border-t border-avalon-card/30">
            注：规则可根据实际游戏约定微调，以录入时的实际得分为准
          </div>
        </div>
      </div>
    </div>
  );
}
